// WEB-2142: клиент импортируется по алиасу '@/api/client', а не './client'.
// Шрам scars/api.md (05.09): относительный путь не подменяется в тестах, и
// любой набор, чей граф импорта задевает этот модуль, падает на разборе
// import.meta внутри настоящего client.ts. Тот же файл, другой путь.
import { api } from '@/api/client';
import type { components } from './generated/schema';

/**
 * WEB-745: server-side pagination + search for GET /projects/:id/quizzes.
 * limit/offset mirror backend PaginationQueryDto (limit 1..100, default 20;
 * offset default 0). `state`/`tags` are the pre-existing filters, now
 * forwarded as real query params instead of client-side-only.
 */
export interface QuizListParams {
  limit?: number;
  offset?: number;
  search?: string;
  state?: string;
  tags?: string[];
}

/**
 * WEB-2142: страница истории версий. Размер общий для квиза и пейволла —
 * окно истории у них одно (ThreePanelEditor), и разъехавшиеся страницы дали бы
 * разное поведение в одном и том же окне.
 */
export const VERSIONS_PAGE_SIZE = 20;

/**
 * WEB-2131 (сервер — WEB-2139): вид версии — публикация либо проверка (снимок
 * кнопки «На проверку»). Тип выведен из контракта ручки списка версий, а не
 * набран руками: новое значение на сервере доедет сюда пересборкой контракта.
 */
export type VersionKind = NonNullable<
  components['schemas']['QuizVersionListItemDto']['kind']
>;

export interface VersionsPageParams {
  limit?: number;
  offset?: number;
  /**
   * WEB-2131: какой вид версий листать. Сервер виды не смешивает — страница и
   * полное число внутри одного вида; без параметра отдаёт публикации.
   */
  kind?: VersionKind;
}

/** WEB-745: lightweight option row for filter dropdowns — GET .../quizzes/options. */
export interface QuizOptionItem {
  id: string;
  name: string;
  slug: string;
  /**
   * WEB-1284: состояние квиза. Нужно селектору направлений A/B-теста —
   * пикер допускает только опубликованный (active) квиз;
   * список остаётся ПОЛНЫМ (та же логика, что у PaywallOptionItem.state,
   * WEB-1152 R2). Optional — старый бэкенд поля не отдавал.
   */
  state?: string;
}

export const quizzesApi = {
  list: (projectId: string, params?: QuizListParams) =>
    api.get(`/projects/${projectId}/quizzes`, { params }),
  // WEB-745: unpaginated {id,name,slug} list for filter dropdowns (BE now
  // caps GET / above at limit=20 by default — dropdowns need every quiz, not
  // just the first page).
  listOptions: (projectId: string) =>
    api.get<QuizOptionItem[]>(`/projects/${projectId}/quizzes/options`),
  create: (projectId: string, data: Record<string, unknown>) =>
    api.post(`/projects/${projectId}/quizzes`, data),
  get: (projectId: string, id: string) =>
    api.get(`/projects/${projectId}/quizzes/${id}`),
  update: (projectId: string, id: string, data: Record<string, unknown>) =>
    api.patch(`/projects/${projectId}/quizzes/${id}`, data),
  publish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/publish`),
  unpublish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/unpublish`),
  duplicate: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/duplicate`),
  delete: (projectId: string, id: string) =>
    api.delete(`/projects/${projectId}/quizzes/${id}`),
  // WEB-411: authenticated draft preview (project member only) — used by the
  // editor "Preview" button so a creator can preview before publish/domain.
  // Atom B (slug→projectId): resolved by-id, matching the public/preview
  // renderer's own fetch (see PublicQuiz.tsx fetchPreviewQuiz).
  preview: (projectId: string, quizSlug: string) =>
    api.get(`/quizzes/preview/by-id/${projectId}/${quizSlug}`),
  // WEB-378: how many ACTIVE funnels will break if this quiz is deleted.
  dependentFunnels: (projectId: string, id: string) =>
    api.get<{ activeFunnels: number }>(
      `/projects/${projectId}/quizzes/${id}/dependent-funnels`,
    ),
  /**
   * WEB-2142: история версий грузится СТРАНИЦЕЙ и без тел снимков — ответ
   * `{ versions, total }`. Без параметров страницу (20) ставит сервер.
   */
  getVersions: (projectId: string, id: string, params?: VersionsPageParams) =>
    api.get(`/projects/${projectId}/quizzes/${id}/versions`, { params }),
  // WEB-2548: читатель — «Посмотреть» в окне истории; ответ по контракту.
  getVersion: (projectId: string, id: string, versionId: string) =>
    api.get<components['schemas']['QuizVersionResponseDto']>(
      `/projects/${projectId}/quizzes/${id}/versions/${versionId}`,
    ),
  /**
   * WEB-2651: `baseUpdatedAt` — отметка версии, которую видит редактор (как у
   * сохранения черновика): сервер сверяет её и отвечает 409 STALE_DRAFT, если
   * страницу успели поменять. Нет отметки — ключа в теле нет, как раньше.
   */
  rollback: (projectId: string, id: string, versionId: string, baseUpdatedAt?: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/rollback`, {
      versionId,
      ...(baseUpdatedAt !== undefined && { baseUpdatedAt }),
    }),
  /**
   * WEB-2138 ч.1 (сервер — WEB-2139): «На проверку» — зафиксировать текущий
   * черновик тестовой версией. `baseUpdatedAt` — метка черновика, на который
   * смотрит кабинет (обязательна: расхождение → 409 STALE_DRAFT, ничего не
   * записано). Ответ `{ version, quiz }`, у квиза — свежий `reviewState`.
   */
  createTestVersion: (projectId: string, id: string, baseUpdatedAt: string) =>
    api.post<components['schemas']['QuizTestVersionResponseDto']>(
      `/projects/${projectId}/quizzes/${id}/test-versions`,
      { baseUpdatedAt } satisfies components['schemas']['CreateTestVersionDto'],
    ),
  /**
   * WEB-4172: «Сохранить версию» — снимок черновика видом `saved` без
   * публикации (сервер — WEB-4120). Метка обязательна (строгая сверка:
   * расхождение → 409 STALE_DRAFT); подпись — только если есть. Строку куска
   * сервер не меняет, ответ — `{ version }`.
   */
  createSavedVersion: (
    projectId: string,
    id: string,
    baseUpdatedAt: string,
    label?: string,
  ) =>
    api.post<components['schemas']['QuizSavedVersionResponseDto']>(
      `/projects/${projectId}/quizzes/${id}/saved-versions`,
      {
        baseUpdatedAt,
        ...(label !== undefined && { label }),
      } satisfies components['schemas']['CreateSavedVersionDto'],
    ),
  /**
   * WEB-4120: «Вернуть в черновик» сохранённую версию (вид `saved`) — без
   * публикации. `baseUpdatedAt` обязательна (строгая сверка: расхождение →
   * 409 STALE_DRAFT). Ответ — квиз со свежим `reviewState`.
   */
  restoreDraft: (
    projectId: string,
    id: string,
    versionId: string,
    baseUpdatedAt: string,
  ) =>
    api.post<components['schemas']['QuizResponseDto']>(
      `/projects/${projectId}/quizzes/${id}/restore-draft`,
      {
        versionId,
        baseUpdatedAt,
      } satisfies components['schemas']['RestoreDraftDto'],
    ),
  move: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/move`, { targetProjectId }),
  copy: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/quizzes/${id}/copy`, { targetProjectId }),
};
